/**
 * The client-side copy of the multiplier curve.
 *
 * The server broadcasts an authoritative multiplier every tick, but ten ticks a
 * second looks like a slideshow next to a 60 Hz display, so the client draws the
 * same curve in between. This is a rendering convenience and nothing more: no
 * payout, no cash-out and no crash is ever decided from it. The server's tick
 * is treated as the truth and any drift is corrected towards it.
 *
 * It mirrors server/internal/game/curve.go: m(t) = 2^(t / doublingSeconds).
 */

export class ClientCurve {
  private doublingSeconds = 10;

  /** Offset between this browser's clock and the server's, in milliseconds. */
  private clockOffsetMs = 0;

  private startedAtServerMs = 0;
  private running = false;

  /** The last authoritative value, and when it arrived. */
  private lastServerX100 = 100;

  configure(doublingSeconds: number): void {
    if (doublingSeconds > 0) this.doublingSeconds = doublingSeconds;
  }

  /**
   * Records the difference between the two clocks.
   *
   * Without this, a browser whose clock is a few seconds off would draw a
   * multiplier wildly out of step with the server's.
   */
  syncClock(serverTimeMs: number): void {
    this.clockOffsetMs = serverTimeMs - Date.now();
  }

  /**
   * This browser's best guess at the server's clock.
   *
   * Anything on screen that counts towards a server deadline -- the betting
   * countdown -- has to read time from here rather than from `Date.now()`. A
   * player whose laptop clock is five minutes fast would otherwise watch the
   * betting bar sit empty through every window.
   */
  serverNow(): number {
    return Date.now() + this.clockOffsetMs;
  }

  start(startedAtServerMs: number): void {
    this.startedAtServerMs = startedAtServerMs;
    this.running = true;
    this.lastServerX100 = 100;
  }

  stop(): void {
    this.running = false;
  }

  /** Called on every server tick, which is what keeps the drawn curve honest. */
  acceptServerTick(x100: number): void {
    this.lastServerX100 = x100;
  }

  /**
   * The multiplier to draw right now.
   *
   * It never returns less than the last value the server sent: the drawn
   * multiplier may lead the server's slightly between ticks, but it must never
   * appear to go backwards, which is what a naive re-sync would look like.
   */
  currentX100(): number {
    if (!this.running) return this.lastServerX100;

    const serverNow = Date.now() + this.clockOffsetMs;
    const elapsedSeconds = (serverNow - this.startedAtServerMs) / 1000;
    if (elapsedSeconds <= 0) return 100;

    const interpolated = Math.floor(Math.pow(2, elapsedSeconds / this.doublingSeconds) * 100);
    return Math.max(this.lastServerX100, Math.max(100, interpolated));
  }
}
